import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import axios from 'axios';
import { baseUrl, baseUrlCSRFCookie } from '../../../resources/Constants';
import { TIPO_USUARIO_LABEL, TIPO_USUARIO_DESC } from '../../../resources/tipoUsuarioLabels';
import './ModalActualizarUsuario.css';

const axiosInstanceCSRFCookie = axios.create({
    baseURL: baseUrlCSRFCookie,
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    withCredentials: true,
    withXSRFToken: true,
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
});

const axiosInstance = axios.create({
    baseURL: baseUrl,
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    withCredentials: true,
    withXSRFToken: true,
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
});

function PasswordField({ id, label, value, onChange, placeholder, minLength }) {
    const [visible, setVisible] = useState(false);

    return (
        <div className="mau-field">
            <label htmlFor={id}>{label}</label>
            <div className="mau-input-wrap">
                <Form.Control
                    id={id}
                    type={visible ? 'text' : 'password'}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    required
                    minLength={minLength}
                    autoComplete="new-password"
                />
                <button
                    type="button"
                    className="mau-toggle-pwd"
                    onClick={() => setVisible((v) => !v)}
                    aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    tabIndex={-1}
                >
                    <i className={`fas fa-eye${visible ? '-slash' : ''}`} />
                </button>
            </div>
        </div>
    );
}

function RoleCard({ tipo, selected, disabled, icon, title, desc, libres, total, onSelect }) {
    const sinCupos = libres <= 0;

    return (
        <button
            type="button"
            className={`mau-role-card${selected ? ' mau-role-card--selected' : ''}${disabled ? ' mau-role-card--disabled' : ''}`}
            onClick={() => !disabled && onSelect(tipo)}
            disabled={disabled}
            aria-pressed={selected}
        >
            <span className="mau-role-card__icon">
                <i className={`fas ${icon}`} />
            </span>
            <span className="mau-role-card__body">
                <div className="mau-role-card__title">{title}</div>
                <div className="mau-role-card__desc">{desc}</div>
                <span className={`mau-role-card__badge ${sinCupos ? 'mau-role-card__badge--full' : 'mau-role-card__badge--ok'}`}>
                    {sinCupos && <span className="mau-role-card__badge-dot" aria-hidden="true" />}
                    {sinCupos
                        ? `Sin cupos (${total}/${total} ocupados)`
                        : `${libres} cupo${libres === 1 ? '' : 's'} disponible${libres === 1 ? '' : 's'} de ${total}`}
                </span>
            </span>
            <span className="mau-role-card__radio" aria-hidden="true" />
        </button>
    );
}

function ModalActualizarUsuario({ show, data, loginOrigin, passwordOrigin, onHide, onSuccess }) {
    const [email, setEmail] = useState(data?.email_actual || '');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [tipoUsuario, setTipoUsuario] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    const medicosDisp = data?.usuarios_medicos_disponibles || 0;
    const medicosCreados = data?.usuarios_medicos_creados || 0;
    const adminDisp = data?.usuarios_administrativos_disponibles || 0;
    const adminCreados = data?.usuarios_administrativos_creados || 0;

    const medicosLibres = medicosDisp - medicosCreados;
    const adminLibres = adminDisp - adminCreados;
    const sinCuposTotal = medicosLibres <= 0 && adminLibres <= 0;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg('');

        if (!email) {
            setErrorMsg('El email es obligatorio');
            return;
        }

        if (newPassword.length < 8) {
            setErrorMsg('La contraseña debe tener al menos 8 caracteres');
            return;
        }

        if (newPassword !== confirmPassword) {
            setErrorMsg('Las contraseñas no coinciden');
            return;
        }

        if (tipoUsuario === '') {
            setErrorMsg('Debe seleccionar el tipo de usuario');
            return;
        }

        setLoading(true);

        try {
            await axiosInstanceCSRFCookie.get('/sanctum/csrf-cookie');
            const response = await axiosInstance.post('/actualizar-usuario', {
                user_id: data.user_id,
                login: loginOrigin,
                password: passwordOrigin,
                email: email,
                new_password: newPassword,
                new_password_confirmation: confirmPassword,
                tipo_usuario: parseInt(tipoUsuario, 10),
            });

            setSuccessMsg(response.data.message || 'Datos actualizados correctamente');
            setTimeout(() => {
                onSuccess();
            }, 3000);
        } catch (error) {
            let msg = 'Ocurrió un error al actualizar los datos';
            if (error.response?.data?.errors) {
                const firstError = Object.values(error.response.data.errors)[0];
                msg = Array.isArray(firstError) ? firstError[0] : firstError;
            } else if (error.response?.data?.message) {
                msg = error.response.data.message;
            }
            setErrorMsg(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={() => {}}
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
            dialogClassName="mau-modal"
        >
            <Modal.Body>
                <header className="mau-header">
                    <div className="mau-header__icon" aria-hidden="true">
                        <i className="fas fa-lock" />
                    </div>
                    <div>
                        <h2 className="mau-header__title">Actualización de Seguridad y Acceso</h2>
                        <p className="mau-header__subtitle">
                            Configure sus credenciales de acceso y seleccione su rol dentro de la institución.
                        </p>
                    </div>
                </header>

                <div className="mau-info" role="note">
                    <i className="fas fa-info-circle" />
                    <span>
                        Por motivos de seguridad, es necesario que configure su correo electrónico,
                        establezca una nueva contraseña y seleccione su tipo de usuario para continuar.
                    </span>
                </div>

                {errorMsg && (
                    <Alert variant="danger" className="mau-alert-inline">
                        {errorMsg}
                    </Alert>
                )}
                {successMsg && (
                    <Alert variant="success" className="mau-alert-inline">
                        {successMsg}
                    </Alert>
                )}

                {!successMsg && (
                    <Form onSubmit={handleSubmit}>
                        <h3 className="mau-section-title">Credenciales de acceso</h3>

                        <div className="mau-field">
                            <label htmlFor="mau-email">Correo electrónico</label>
                            <Form.Control
                                id="mau-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="doctor.martinez@clinicadental.com"
                                required
                                className="mau-email-input"
                            />
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <PasswordField
                                    id="mau-password"
                                    label="Nueva contraseña"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Mínimo 8 caracteres"
                                    minLength={8}
                                />
                            </div>
                            <div className="col-md-6">
                                <PasswordField
                                    id="mau-password-confirm"
                                    label="Confirmar contraseña"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Repita su contraseña"
                                    minLength={8}
                                />
                            </div>
                        </div>

                        <h3 className="mau-section-title mt-2">Selecciona tu rol</h3>
                        <p className="text-muted small mb-3">
                            Elige el tipo de usuario que mejor describe tu función en la institución.
                        </p>

                        <RoleCard
                            tipo="0"
                            selected={tipoUsuario === '0'}
                            disabled={medicosLibres <= 0}
                            icon="fa-stethoscope"
                            title={TIPO_USUARIO_LABEL['0']}
                            desc={TIPO_USUARIO_DESC['0']}
                            libres={medicosLibres}
                            total={medicosDisp}
                            onSelect={setTipoUsuario}
                        />

                        <RoleCard
                            tipo="1"
                            selected={tipoUsuario === '1'}
                            disabled={adminLibres <= 0}
                            icon="fa-clipboard-list"
                            title={TIPO_USUARIO_LABEL['1']}
                            desc={TIPO_USUARIO_DESC['1']}
                            libres={adminLibres}
                            total={adminDisp}
                            onSelect={setTipoUsuario}
                        />

                        {sinCuposTotal && (
                            <div className="mau-alert-error" role="alert">
                                <i className="fas fa-exclamation-circle" />
                                <div>
                                    <div className="mau-alert-error__title">Sin cupos disponibles</div>
                                    <p className="mau-alert-error__text">
                                        No hay cupos disponibles para ningún rol en esta institución.
                                        Contacte al administrador del sistema para solicitar acceso adicional.
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="mau-footer">
                            <Button
                                type="button"
                                variant="light"
                                className="mau-btn-cancel"
                                onClick={() => window.location.reload()}
                            >
                                Cancelar e ir atrás
                            </Button>
                            <Button
                                type="submit"
                                className="mau-btn-primary"
                                disabled={loading || sinCuposTotal}
                            >
                                {loading ? 'Guardando…' : 'Guardar y Continuar'}
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal.Body>
        </Modal>
    );
}

export default ModalActualizarUsuario;
